import {Box, Paper, Typography} from '@mui/material';
import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Player, PlayerSessionsReportSession} from '../../interfaces';
import {getTeamPlayerSessionsReport} from '../../api/reportAPI';
import {useParams} from 'react-router-dom';
import {useAppSelector} from '../../store/hooks';
import {DataGrid, GridColDef} from '@mui/x-data-grid';
import {downloadReportCsv} from '../../services/reportService';
import TeamReportsFilter from './TeamReportsFilter';
import {
  getSpeedMultiplier,
  getSpeedUnit,
} from '../../services/measurementService';
import {getTime} from '../../services/dateService';
import {DateTime} from 'luxon';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import {TFunction} from 'i18next';

const getColumns = (t: TFunction, preferredUnits?: string): GridColDef[] => {
  const speedUnit = getSpeedUnit(preferredUnits === 'metric');
  return [
    {
      field: 'firstName',
      headerName: t('profile.firstName') as string,
      filterable: true,
    },
    {
      field: 'lastName',
      headerName: t('profile.lastName') as string,
      filterable: true,
    },
    {
      field: 'position',
      headerName: t('profile.position') as string,
      filterable: true,
    },
    {
      field: 'number',
      headerName: t('profile.number') as string,
      filterable: true,
    },
    {
      field: 'teamSessionId',
      headerName: t('export.teamSessionId') as string,
      filterable: true,
    },
    {
      field: 'type',
      headerName: t('sessionStats.sessionType') as string,
      filterable: true,
    },
    {
      field: 'localDisplayStartTime',
      headerName: t('sessionStats.localDisplayStartTime') as string,
      filterable: true,
    },
    {
      field: 'startTime',
      headerName: t('sessionStats.startTime') as string,
      filterable: true,
    },
    {
      field: 'endTime',
      headerName: t('sessionStats.endTime') as string,
      filterable: true,
    },
    {
      field: 'activeTime',
      headerName: t('teamSessionPlayerList.activeTime') as string,
      filterable: true,
    },
    {
      field: 'strintTotalTime',
      headerName: t('teamSessionPlayerList.strintTotalTime') as string,
      filterable: true,
    },
    {
      field: 'strideCountLeft',
      headerName: t('teamSessionPlayerList.strideCountLeft') as string,
      filterable: true,
    },
    {
      field: 'strideCountRight',
      headerName: t('teamSessionPlayerList.strideCountRight') as string,
      filterable: true,
    },
    {
      field: 'speedAvgV2',
      headerName: t('teamSessionPlayerList.speedAvg', {
        units: speedUnit,
      }) as string,
      filterable: true,
    },
    {
      field: 'speedMaxV2',
      headerName: t('teamSessionPlayerList.speedMax', {
        units: speedUnit,
      }) as string,
      filterable: true,
    },
    {
      field: 'accelAvgV2',
      headerName: t('teamSessionPlayerList.accelAvg') as string,
      filterable: true,
    },
    {
      field: 'accelMaxV2',
      headerName: t('teamSessionPlayerList.accelMax') as string,
      filterable: true,
    },
    {
      field: 'strintFilteredStrideRateAvgV2',
      headerName: t('teamSessionPlayerList.agilityAvg') as string,
      filterable: true,
    },
    {
      field: 'strintFilteredStrideRateMaxV2',
      headerName: t('teamSessionPlayerList.agilityMax') as string,
      filterable: true,
    },
    {
      field: 'balanceNormalized',
      headerName: t('teamSessionPlayerList.balanceNormalized') as string,
      filterable: true,
    },
    {
      field: 'calculatedShiftCount',
      headerName: t('teamSessionPlayerList.shiftCount') as string,
      filterable: true,
    },
    {
      field: 'shiftTimeAvg',
      headerName: t('teamSessionPlayerList.shiftTimeAvg') as string,
      filterable: true,
    },
    {
      field: 'hrAvg',
      headerName: t('teamSessionPlayerList.hrAvg') as string,
      filterable: true,
    },
    {
      field: 'hrMax',
      headerName: t('teamSessionPlayerList.hrMax') as string,
      filterable: true,
    },
    {
      field: 'energyRatio',
      headerName: t('teamSessionPlayerList.energyRatio') as string,
      filterable: true,
    },
    {
      field: 'hustleScore',
      headerName: t('teamSessionPlayerList.hustleScore') as string,
      filterable: true,
    },
    {
      field: 'cumulative3DPlayerLoadV2',
      headerName: t('teamSessionPlayerList.playerLoad') as string,
      filterable: true,
    },
    {
      field: 'loadPerMinute',
      headerName: t('statistics.loadPerMinute') as string,
      filterable: true,
    },
  ];
};

interface Props {
  players: Player[];
  preferredUnits?: string;
}

const TeamPlayerSessionsReport: React.FC<Props> = ({
  players,
  preferredUnits,
}) => {
  const {t} = useTranslation();
  const {teamId} = useParams();
  const activeTeam = (useAppSelector(state => state.team.teams) ?? []).find(
    team => team.id === parseInt(teamId ?? ''),
  );

  const [reportData, setReportData] = useState<
    PlayerSessionsReportSession[] | null
  >(null);
  const [loading, setLoading] = useState(false);

  const columns = useMemo(
    () => getColumns(t, preferredUnits),
    [preferredUnits, t],
  );

  const onSubmit = useCallback(
    async ({
      startDate,
      endDate,
      profileIds,
      sessionTypes,
    }: {
      startDate: Date;
      endDate: Date;
      profileIds: number[];
      sessionTypes: string[];
    }) => {
      setLoading(true);
      const response = await getTeamPlayerSessionsReport({
        teamId: parseInt(teamId ?? ''),
        startDate,
        endDate,
        profileIds,
        sessionTypes,
      });
      setLoading(false);
      setReportData(
        (response ?? []).map(ps => {
          // Calculate load per minute: (cumulative3DPlayerLoadV2 / 100) / (activeTime / 60)
          let loadPerMinute: number | null = null;
          if (
            ps.cumulative3DPlayerLoadV2 != null &&
            ps.activeTime != null &&
            ps.activeTime > 0
          ) {
            const playerLoad = ps.cumulative3DPlayerLoadV2 / 100;
            const activeTimeMinutes = ps.activeTime / 60;
            loadPerMinute = Math.round(playerLoad / activeTimeMinutes);
          }

          return {
            id: ps.id,
            firstName: ps.firstName,
            lastName: ps.lastName,
            position: ps.position,
            number: ps.number,
            teamSessionId: ps.teamSessionId,
            type: ps.type,
            localDisplayStartTime: ps.localDisplayStartTime,
            startTime: ps.startTime,
            endTime: ps.endTime,
            activeTime: ps.activeTime
              ? parseFloat(ps.activeTime.toFixed(2))
              : ps.activeTime,
            strintTotalTime: ps.strintTotalTime
              ? parseFloat(ps.strintTotalTime.toFixed(2))
              : ps.strintTotalTime,
            calculatedShiftCount: ps.calculatedShiftCount,
            shiftTimeAvg: ps.shiftTimeAvg
              ? parseFloat(ps.shiftTimeAvg.toFixed(2))
              : ps.shiftTimeAvg,
            strideCountLeft: ps.strideCountLeft,
            strideCountRight: ps.strideCountRight,
            speedAvgV2: ps.speedAvgV2
              ? parseFloat(
                  (
                    ps.speedAvgV2 *
                    getSpeedMultiplier(preferredUnits === 'metric')
                  ).toFixed(2),
                )
              : ps.speedAvgV2,
            speedMaxV2: ps.speedMaxV2
              ? parseFloat(
                  (
                    ps.speedMaxV2 *
                    getSpeedMultiplier(preferredUnits === 'metric')
                  ).toFixed(2),
                )
              : ps.speedMaxV2,
            accelAvgV2: ps.accelAvgV2
              ? parseFloat(((ps.accelAvgV2 ?? 0) / 9.8).toFixed(2))
              : ps.accelAvgV2,
            accelMaxV2: ps.accelMaxV2
              ? parseFloat(((ps.accelMaxV2 ?? 0) / 9.8).toFixed(2))
              : ps.accelMaxV2,
            strintFilteredStrideRateAvgV2: ps.strintFilteredStrideRateAvgV2
              ? parseFloat(ps.strintFilteredStrideRateAvgV2.toFixed(2))
              : ps.strintFilteredStrideRateAvgV2,
            strintFilteredStrideRateMaxV2: ps.strintFilteredStrideRateMaxV2
              ? parseFloat(ps.strintFilteredStrideRateMaxV2.toFixed(2))
              : ps.strintFilteredStrideRateMaxV2,
            balanceNormalized: ps.balanceNormalized,
            energyRatio: ps.energyRatio,
            hustleScore: ps.hustleScore,
            cumulative3DPlayerLoadV2: ps.cumulative3DPlayerLoadV2
              ? parseFloat((ps.cumulative3DPlayerLoadV2 / 100).toFixed(0))
              : ps.cumulative3DPlayerLoadV2,
            loadPerMinute,
          };
        }) ?? [],
      );
    },
    [preferredUnits, teamId],
  );

  const onReset = useCallback(() => {
    setReportData(null);
  }, []);

  const onDownload = useCallback(() => {
    if (!reportData) return;

    // update property names of reportData using columns
    const exportReportData = (
      reportData as Record<string, string | number | null>[]
    ).map(row => {
      const modifiedRow = {...row};
      // change energyRatio to percentage
      modifiedRow.energyRatio = modifiedRow.energyRatio
        ? `${((modifiedRow.energyRatio as number) * 100).toFixed(0)}%`
        : null;
      // change balance to percentage
      modifiedRow.balanceNormalized = modifiedRow.balanceNormalized
        ? `${((modifiedRow.balanceNormalized as number) * 100).toFixed(0)}%`
        : null;
      const updatedRow = {...modifiedRow};
      Object.keys(modifiedRow).forEach(oldKey => {
        const newKey =
          columns.find(column => column.field === oldKey)?.headerName ?? '';
        // Copy the value to the new key
        updatedRow[newKey] = modifiedRow[oldKey as keyof typeof modifiedRow];
        // Delete the old key
        delete updatedRow[oldKey as keyof typeof updatedRow];
      });
      // remove id from the row
      delete updatedRow.id;
      return updatedRow;
    });

    downloadReportCsv({
      exportReportData,
      teamName: activeTeam?.name ?? '',
      reportName: 'player_sessions',
    });
  }, [activeTeam?.name, columns, reportData]);

  return (
    <Paper sx={{p: 5}}>
      <Typography variant="h3" sx={{mb: 2}}>
        {t('pages.playerSessions')}
      </Typography>
      <TeamReportsFilter
        players={players}
        onDownload={reportData ? onDownload : undefined}
        onReset={onReset}
        onSubmit={onSubmit}
      />
      {loading && <FullScreenLoadingSpinner />}
      {reportData && (
        <Box sx={{height: 700}}>
          <DataGrid
            rows={(reportData ?? []).map(row => ({
              ...row,
              startTime: row.startTime
                ? DateTime.fromISO(row.startTime).toFormat('M/d/yyyy h:mm a')
                : null,
              endTime: row.endTime
                ? DateTime.fromISO(row.endTime).toFormat('M/d/yyyy h:mm a')
                : null,
              activeTime: getTime(row.activeTime),
              strintTotalTime: getTime(row.strintTotalTime),
              shiftTimeAvg: getTime(row.shiftTimeAvg),
              energyRatio: row.energyRatio
                ? `${(row.energyRatio * 100).toFixed()}%`
                : null,
              balanceNormalized: row.balanceNormalized
                ? `${(row.balanceNormalized * 100).toFixed(0)}%`
                : null,
            }))}
            columns={columns}
          />
        </Box>
      )}
    </Paper>
  );
};

export default TeamPlayerSessionsReport;
